import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  keyframes,
} from "@mui/material";
import {
  Close as CloseIcon,
  EmojiEvents as TrophyIcon,
  Replay as ReplayIcon,
} from "@mui/icons-material";

const popIn = keyframes`
  0% { opacity: 0; transform: scale(0.72) translateY(18px); }
  70% { opacity: 1; transform: scale(1.04) translateY(-2px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const trophyBounce = keyframes`
  0%, 100% { transform: translateY(0) rotate(-4deg); }
  50% { transform: translateY(-8px) rotate(4deg); }
`;

const fillBar = keyframes`
  from { width: 0%; }
  to { width: var(--score-pct); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

/**
 * Game over results — stays open until the user closes it.
 * Claps are owned by the parent and should stop on onClose.
 */
const GameOverResultsModal = ({
  open,
  onClose,
  score = 0,
  outOf = 0,
  subtitle = null,
  playerLabel = null,
  scoreCaption = "Your score",
  actionLabel = null,
  onAction = null,
  hideClose = false,
}) => {
  const safeOut = Math.max(0, Number(outOf) || 0);
  const safeScore = Math.max(0, Number(score) || 0);
  const pct = safeOut > 0 ? Math.min(100, Math.round((safeScore / safeOut) * 100)) : 0;

  let rating = "Nice try";
  let ratingColor = "#90a4ae";
  if (safeOut > 0) {
    if (pct >= 100) {
      rating = "Perfect run!";
      ratingColor = "#ffd54f";
    } else if (pct >= 70) {
      rating = "Strong finish";
      ratingColor = "#81c784";
    } else if (pct >= 40) {
      rating = "Good effort";
      ratingColor = "#64b5f6";
    }
  }

  return (
    <Dialog
      open={!!open}
      onClose={hideClose ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={!!hideClose}
      BackdropProps={{
        sx: {
          background:
            "radial-gradient(ellipse at center, rgba(255,193,7,0.18) 0%, rgba(0,0,0,0.82) 65%)",
          backdropFilter: "blur(4px)",
        },
      }}
      PaperProps={{
        sx: {
          overflow: "hidden",
          borderRadius: { xs: 3, sm: 4 },
          border: "1px solid rgba(255,213,79,0.35)",
          background:
            "linear-gradient(165deg, #2a2210 0%, #141414 42%, #0d0d0d 100%)",
          boxShadow:
            "0 24px 64px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,213,79,0.08), inset 0 1px 0 rgba(255,255,255,0.06)",
          animation: `${popIn} 0.45s cubic-bezier(0.22, 1, 0.36, 1)`,
        },
      }}
    >
      {/* Top gold ribbon */}
      <Box
        sx={{
          height: 5,
          background:
            "linear-gradient(90deg, #8d6e00, #ffd54f, #fff8e1, #ffd54f, #8d6e00)",
          backgroundSize: "200% 100%",
          animation: `${shimmer} 2.8s linear infinite`,
        }}
      />

      {hideClose ? null : (
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 10,
          top: 14,
          zIndex: 2,
          color: "rgba(255,255,255,0.55)",
          bgcolor: "rgba(255,255,255,0.04)",
          "&:hover": { bgcolor: "rgba(255,255,255,0.1)", color: "#fff" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      )}

      <DialogContent sx={{ textAlign: "center", pt: 3.5, pb: 3.5, px: { xs: 2.5, sm: 3.5 } }}>
        <Box
          sx={{
            width: 84,
            height: 84,
            mx: "auto",
            mb: 2,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            background:
              "radial-gradient(circle at 35% 30%, rgba(255,213,79,0.35), rgba(255,193,7,0.08) 55%, transparent 70%)",
            border: "2px solid rgba(255,213,79,0.45)",
            boxShadow: "0 0 28px rgba(255,193,7,0.22)",
            animation: `${trophyBounce} 1.8s ease-in-out infinite`,
          }}
        >
          <TrophyIcon sx={{ fontSize: 42, color: "#ffd54f" }} />
        </Box>

        <Typography
          variant="overline"
          sx={{
            letterSpacing: 3,
            color: "rgba(255,213,79,0.85)",
            fontWeight: 700,
            animation: `${fadeUp} 0.4s ease both`,
            animationDelay: "0.05s",
          }}
        >
          Higher or Lower
        </Typography>

        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            fontSize: { xs: "2rem", sm: "2.35rem" },
            letterSpacing: "-0.02em",
            mt: 0.5,
            mb: 0.5,
            background: "linear-gradient(180deg, #fff8e1 0%, #ffd54f 55%, #ffb300 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: `${fadeUp} 0.45s ease both`,
            animationDelay: "0.1s",
          }}
        >
          Game Over!
        </Typography>

        {playerLabel ? (
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.05rem",
              color: "#fff",
              mb: 0.5,
              animation: `${fadeUp} 0.45s ease both`,
              animationDelay: "0.12s",
            }}
          >
            {playerLabel}
          </Typography>
        ) : null}

        <Typography
          sx={{
            color: ratingColor,
            fontWeight: 700,
            fontSize: "0.95rem",
            mb: 2.5,
            animation: `${fadeUp} 0.45s ease both`,
            animationDelay: "0.15s",
          }}
        >
          {rating}
        </Typography>

        {/* Score card */}
        <Box
          sx={{
            mx: "auto",
            maxWidth: 300,
            p: 2.25,
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            animation: `${fadeUp} 0.5s ease both`,
            animationDelay: "0.2s",
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            {scoreCaption}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "center",
              gap: 1,
              mb: 1.75,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: { xs: "3rem", sm: "3.4rem" },
                fontWeight: 900,
                lineHeight: 1,
                color: "#fff",
              }}
            >
              {safeScore}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: "1.35rem",
                fontWeight: 600,
                color: "rgba(255,255,255,0.4)",
              }}
            >
              out of {safeOut}
            </Typography>
          </Box>

          <Box
            sx={{
              height: 10,
              borderRadius: 999,
              bgcolor: "rgba(255,255,255,0.08)",
              overflow: "hidden",
              "--score-pct": `${pct}%`,
            }}
          >
            <Box
              sx={{
                height: "100%",
                borderRadius: 999,
                background: "linear-gradient(90deg, #c79100, #ffd54f, #fff3c4)",
                animation: open ? `${fillBar} 0.9s cubic-bezier(0.22, 1, 0.36, 1) both` : "none",
                animationDelay: "0.25s",
                boxShadow: "0 0 12px rgba(255,213,79,0.45)",
              }}
            />
          </Box>

          <Typography
            sx={{
              mt: 1,
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "rgba(255,213,79,0.9)",
            }}
          >
            {pct}% correct
          </Typography>
        </Box>

        {subtitle ? (
          <Typography
            variant="body2"
            sx={{
              mt: 2,
              color: "rgba(255,255,255,0.5)",
              lineHeight: 1.45,
              maxWidth: 320,
              mx: "auto",
              animation: `${fadeUp} 0.5s ease both`,
              animationDelay: "0.3s",
            }}
          >
            {subtitle}
          </Typography>
        ) : null}

        {!hideClose || (actionLabel && onAction) ? (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            gap: 1.25,
            justifyContent: "center",
            flexWrap: "wrap",
            animation: `${fadeUp} 0.55s ease both`,
            animationDelay: "0.35s",
          }}
        >
          {actionLabel && onAction ? (
            <Button
              variant="contained"
              startIcon={<ReplayIcon />}
              onClick={onAction}
              sx={{
                px: 2.75,
                py: 1.1,
                fontWeight: 800,
                borderRadius: 2,
                textTransform: "none",
                bgcolor: "#ffd54f",
                color: "#1a1400",
                boxShadow: "0 8px 24px rgba(255,213,79,0.28)",
                "&:hover": { bgcolor: "#ffe082" },
              }}
            >
              {actionLabel}
            </Button>
          ) : null}
          {hideClose ? null : (
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              px: 2.75,
              py: 1.1,
              fontWeight: 700,
              borderRadius: 2,
              textTransform: "none",
              color: "#fff",
              borderColor: "rgba(255,255,255,0.28)",
              "&:hover": {
                borderColor: "rgba(255,255,255,0.5)",
                bgcolor: "rgba(255,255,255,0.06)",
              },
            }}
          >
            Close
            </Button>
          )}
        </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default GameOverResultsModal;
